import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChatCircleDots,
  Lightbulb,
  Headset,
  EnvelopeSimple,
  Microphone,
  Crown,
  Users,
  Scales,
  ArrowRight,
  CheckCircle,
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';

// Free check-in scheduler — opens HubSpot in a new tab (no embedded popup).
const CHECKIN_URL = 'https://meetings-na2.hubspot.com/reyna-fisher/client-check-in-with-indie-law';

export default function BookCallPage() {
  const { user } = useAuth();
  const isProtectionPlan = !!user?.has_protection_plan;
  const [booked, setBooked] = useState(false);
  // Strategy-call booking page (opens in a new tab — HubSpot popups aren't reliable).
  const strategyUrl = isProtectionPlan
    ? 'https://meetings-na2.hubspot.com/joey-vitale/protection-plan-call'
    : 'https://meetings-na2.hubspot.com/joey-vitale/legal-strategy-call';


  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="book-call-page">

        {/* Page header */}
        <div className="mb-3">
          <p className="label-uppercase text-[#52525B] mb-0.5">Talk to Us</p>
          <h1 className="text-2xl font-bold text-[#09090B] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Book a Call
          </h1>
        </div>

        {/* Communication preference — compact */}
        <div className="mb-4 px-3 py-2.5 bg-[#f2e9db]/60 border border-[#141b4d]/15" data-testid="comm-preference-callout">
          <div className="flex items-start gap-2.5">
            <EnvelopeSimple weight="duotone" className="w-4 h-4 text-[#141b4d] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#3f3f46] leading-relaxed">
              Most clients prefer to keep as much back-and-forth in writing as possible — it's easy to search your
              inbox later. You can always email us at{' '}
              <a href="mailto:clients@indielaw.com" className="text-[#1d57a5] hover:underline font-medium">clients@indielaw.com</a>.{' '}
              <Microphone weight="duotone" className="w-3.5 h-3.5 inline align-text-bottom text-[#141b4d]" /> If you do book a
              call, <strong className="text-[#09090B]">we'll record it</strong> and send a written follow-up so nothing falls through the cracks.
            </p>
          </div>
        </div>

        {/* Two columns — both options visible without scrolling */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">

          {/* Option 1 — Free check-in */}
          <section className="card-swiss flex flex-col h-full p-5" style={{ backgroundColor: '#eef1f7', borderColor: '#9aa6c3' }} data-testid="checkin-section">
            <span className="label-uppercase text-[10px] font-semibold px-2 py-0.5 self-start" style={{ backgroundColor: '#006747', color: '#ffffff' }}>
              Free · Recommended first
            </span>
            <h2 className="text-xl font-bold mt-2 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#141b4d' }}>
              Free Check-In Call with Client Care
            </h2>
            <p className="text-sm leading-snug mb-3" style={{ color: '#101820' }}>
              Anytime you want to talk something through with a real person, book a free check-in with our non-attorney
              client care team. This is usually the right starting point — most questions are answered here without a paid strategy call.
            </p>
            <p className="font-semibold text-sm mb-1.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#141b4d' }}>Great for talking through:</p>
            <ul className="space-y-1.5 mb-3">
              {["Details of your trademark application", "A search results letter you've received", "An Office Action from the USPTO", "A status update or anything you've seen on this portal"].map((text, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: '#101820' }}>
                  <ChatCircleDots weight="duotone" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#006747' }} />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-2.5 text-xs leading-snug mb-4" style={{ color: '#101820' }}>
              <Scales weight="duotone" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#141b4d' }} />
              <p><strong style={{ color: '#141b4d' }}>Lawyers stay involved.</strong> Our attorneys brief the client care team before your call and review afterward, so next steps always line up with their legal advice.</p>
            </div>
            <a href={CHECKIN_URL} target="_blank" rel="noopener noreferrer" data-testid="checkin-book-btn"
              onClick={() => setBooked(true)}
              className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3 text-sm font-bold transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#006747', color: '#ffffff' }}>
              Book a free check-in call <ArrowRight weight="bold" className="w-4 h-4" />
            </a>
          </section>

          {/* Option 2 — Strategy call */}
          <section className="card-swiss flex flex-col h-full p-5" data-testid="strategy-section">
            {isProtectionPlan ? (
              <span className="label-uppercase text-[10px] font-semibold px-2 py-0.5 self-start" style={{ backgroundColor: '#006747', color: '#ffffff' }} data-testid="strategy-badge-pp">
                Free · Included with Protection Plan
              </span>
            ) : (
              <span className="label-uppercase text-[10px] font-semibold px-2 py-0.5 self-start bg-[#FFD60A]/30 text-[#a17900] border border-[#FFD60A]/50" data-testid="strategy-badge-paid">
                Paid · Direct with a lawyer
              </span>
            )}
            <h2 className="text-xl font-bold text-[#09090B] mt-2 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {isProtectionPlan ? 'Strategy Call with a Lawyer' : 'Paid Strategy Call with a Lawyer'}
            </h2>

            {isProtectionPlan ? (
              <>
                <p className="text-sm leading-snug text-[#3f3f46] mb-3">
                  As a Protection Plan member, you get one 30-minute strategy call with a lawyer included <em>per quarter</em> at no charge. Book yours whenever you'd like to talk directly with an attorney.
                </p>
                <div className="p-3 mb-3" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }} data-testid="pp-strategy-benefit">
                  <div className="flex items-start gap-2.5">
                    <Crown weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-700" />
                    <div>
                      <p className="font-semibold text-[#14532d] mb-0.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>One free strategy call per quarter</p>
                      <p className="text-xs leading-snug text-[#14532d]">Your Protection Plan covers this call in full. Non-members pay $350 per call.</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm leading-snug text-[#3f3f46] mb-3">
                  For situations where you'd like to talk directly with a lawyer, book a 30-minute paid strategy call.
                </p>
                <div className="p-3 bg-[#FFD60A]/10 border border-[#FFD60A]/40 mb-3">
                  <div className="flex items-baseline justify-between mb-0.5 flex-wrap gap-2">
                    <p className="font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Client rate</p>
                    <p className="text-2xl font-bold text-[#141b4d]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>$350</p>
                  </div>
                  <p className="text-xs text-[#52525B]">That's $200 less than the standard non-client rate of $550 — a perk of being part of the Indie Law family.</p>
                </div>
              </>
            )}

            <p className="font-semibold text-sm mb-1.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Good fit if you want to talk through:</p>
            <ul className="space-y-1.5 mb-3">
              {[{ icon: Lightbulb, text: "Strategy around a potential new trademark" }, { icon: Users, text: "Expanding or pivoting into a new product line or industry" }, { icon: Headset, text: "How to handle a copycat or infringer" }].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#3f3f46]">
                  <Icon weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#141b4d]" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>

            {!isProtectionPlan && (
              <div className="p-3 mb-3" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}>
                <div className="flex items-start gap-2.5 mb-2">
                  <Crown weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-700" />
                  <p className="text-xs leading-snug text-[#14532d]"><strong>Protection Plan clients</strong> get one strategy call with a lawyer included <em>per quarter</em> at no charge.</p>
                </div>
                <Link to="/protection-plan" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-emerald-700 text-white hover:bg-emerald-800 transition-colors" data-testid="pp-cta-from-book-call">
                  Learn more about Protection Plan <ArrowRight weight="bold" className="w-3 h-3" />
                </Link>
              </div>
            )}

            <a href={strategyUrl} target="_blank" rel="noopener noreferrer" data-testid="strategy-book-btn"
              onClick={() => setBooked(true)}
              className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3 text-sm font-bold transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#1d57a5', color: '#ffffff' }}>
              {isProtectionPlan ? 'Book your included strategy call' : 'Book a paid strategy call'} <ArrowRight weight="bold" className="w-4 h-4" />
            </a>
          </section>
        </div>

        {/* Reassurance note — appears right after a client opens a scheduler */}
        <AnimatePresence>
          {booked && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              data-testid="booking-followup-note"
              className="mt-4 flex items-start gap-2.5 px-4 py-3 text-sm"
              style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', color: '#14532d' }}>
              <CheckCircle weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-700" />
              <p>
                Once you pick a time, you're all set — <strong>we'll email you a written follow-up after your call</strong> so
                nothing falls through the cracks. Prefer email in the meantime? Reach us at{' '}
                <a href="mailto:clients@indielaw.com" className="underline font-medium">clients@indielaw.com</a>.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
